/**
 * Avatar face picker component with image grid
 */
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, UserCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '../../../lib/utils';
import { Card } from '../../../components/ui/card';
import { avatarFacesApi } from '../../../modules/avatars/services/avatarFacesApi';
import { EntityLoadState } from '../common';
import type { AvatarFace } from '../../../modules/avatars/types';

interface AvatarFacePickerProps {
  value?: string;
  onChange: (faceId: string | null) => void;
  disabled?: boolean;
}

export const AvatarFacePicker = ({
  value,
  onChange,
  disabled = false,
}: AvatarFacePickerProps) => {
  const { t } = useTranslation('common');
  const { data: faces = [], isLoading, error } = useQuery({
    queryKey: ['avatarFaces'],
    queryFn: avatarFacesApi.listFaces,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <EntityLoadState error={error} className="mb-4" />
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-4">
      <Card
        className={cn(
          'relative overflow-hidden transition-all cursor-pointer',
          disabled && 'opacity-60 cursor-not-allowed',
          !value
            ? 'ring-2 ring-primary border-primary'
            : 'hover:border-primary/50'
        )}
        onClick={() => !disabled && onChange(null)}
      >
        <div className="aspect-square flex items-center justify-center bg-muted">
          <UserCircle className="h-12 w-12 text-muted-foreground" />
        </div>
        <div className="p-2">
          <p className="text-sm text-center truncate">{t('avatars.none')}</p>
        </div>
        {!value && (
          <div className="absolute top-1 right-1">
            <CheckCircle className="h-5 w-5 text-primary bg-white rounded-full" />
          </div>
        )}
      </Card>
      {faces.map((face: AvatarFace) => (
        <Card
          key={face.id}
          className={cn(
            'relative overflow-hidden transition-all cursor-pointer',
            disabled && 'opacity-60 cursor-not-allowed',
            value === face.id
              ? 'ring-2 ring-primary border-primary'
              : 'hover:border-primary/50'
          )}
          onClick={() => !disabled && onChange(face.id)}
        >
          <div className="aspect-square">
            <img
              src={face.preview_url || '/images/avatars/default.png'}
              alt={face.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-2">
            <p className="text-sm text-center truncate">{face.name}</p>
          </div>
          {value === face.id && (
            <div className="absolute top-1 right-1">
              <CheckCircle className="h-5 w-5 text-primary bg-white rounded-full" />
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};
